import { operatorSymbolsJson } from "@corca/data";

import { getPhantomHints } from "@/components/suggestions/providers/utils/getPhantomHints";
import { prioritizeSuggestionRules } from "@/input/utils/editOperators";
import { isUnitChild } from "@/models/nodes/utils";

import { isSlashMenuActive } from "./slashCommandSuggestionsProvider";
import { appendLatexKeywords } from "./utils/appendLatexKeywords";
import { filterSuggestions } from "./utils/filterSuggestions";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";
import { getAutocompleteProps, TEXT_SYMBOLS } from "./utils/operatorAutocomplete";

import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";

export const symbolJson: SingleSuggestion<"operatorSymbol">[] = operatorSymbolsJson.map((s) => ({
  type: "single",
  item: {
    ...s,
    type: "operatorSymbol",
    props: s.props,
    keywords: appendLatexKeywords(s.key, normalizeSuggestionKeywords(s)),
    intlName: "operatorSymbol",
    displayName: s.name,
    value: undefined,
  },
  priorityRules: s.priorityRules,
  canAutocomplete: s.canAutocomplete,
  canBeAutocompleted: s.canBeAutocompleted ?? getAutocompleteProps(s.symbol),
  priority: s.priority ?? 1,
  inputMode: s.inputMode,
  hideStandalone: s.hideStandalone,
}));

const UNIT_CONTEXT_SYMBOL_JSON = symbolJson.filter((s) => TEXT_SYMBOLS.includes(s.item.symbol));

export const SYMBOLS_AUTOCOMPLETE_REGEX = new RegExp(
  symbolJson
    .map((s) => {
      const autocomplete = s.canBeAutocompleted;
      if (
        typeof autocomplete === "object" &&
        autocomplete?.text &&
        typeof autocomplete.text === "object"
      ) {
        return autocomplete.text.regex;
      }
      return undefined;
    })
    .filter((regex): regex is string => Boolean(regex))
    .map((regex) => regex.replace(/^\^|\$$/g, "").replace(/[()]/g, ""))
    .join("|"),
);

const PHANTOM_HINT_TRIGGER_SYMBOLS = new Set(["=", "∈"]);

export const operatorSymbolSuggestionsProvider: SuggestionProvider = {
  type: "operatorSymbol",
  search: (str, doc, lang) => {
    if (!str) {
      return [];
    }

    if (doc.selection.hasTextSelection || doc.libraries.serves("operator-symbols.json")) {
      return [];
    }

    if (isSlashMenuActive(str, doc)) {
      return [];
    }

    const input = str.toLowerCase();
    const inputMode = doc.editor.editorState.isExpression ? "math" : "text";

    // Inside a unit bracket only the arithmetic/punctuation symbols make sense; named symbols
    // (Sine Wave, Greater Than, ...) must not leak into unit suggestions.
    const symbols = isUnitChild(doc.cursor.node) ? UNIT_CONTEXT_SYMBOL_JSON : symbolJson;

    let filteredOptions = filterSuggestions(input, symbols, { lang, inputMode });

    filteredOptions = filteredOptions.map((s) => {
      const priority = prioritizeSuggestionRules(s, doc.cursor);

      if (s.type === "single" && PHANTOM_HINT_TRIGGER_SYMBOLS.has(s.item.symbol)) {
        return {
          ...s,
          phantomHints: getPhantomHints(doc, s.item.symbol),
          priority,
        };
      }

      return { ...s, priority };
    });

    return [filteredOptions];
  },
};
